import React, { useState } from 'react';
import './feature.css';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import StarIcon from '../../components/starIcon/StarIcon';

function FeatureForm({ createFeature, addFeature }) {
  const [formData, setFormData] = useState({
    name: '',
    apiPath: '',
    description: '',
    action: '',
    isDefault: false, // Added isDefault
    displayName: '',  // Added displayName
  });
  const [errors, setErrors] = useState({});
  const [selectedAction, setSelectedAction] = useState('');

  // Handler for action dropdown
  const handleActionChange = (e) => {
    setSelectedAction(e.target.value);
    const updatedErrors = { ...errors };
    delete updatedErrors.action;
    setErrors(updatedErrors);
    setFormData((prevData) => ({
      ...prevData,
      action: e.target.value,
    }));
  };

  // Handler for text fields and checkbox
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    const updatedErrors = { ...errors };
    delete updatedErrors[name];
    setErrors(updatedErrors);

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.name.trim()) errors.name = 'Please enter a name.';
    if (!formData.apiPath.trim()) errors.apiPath = 'Please enter an API path.';
    if (!formData.displayName.trim()) errors.displayName = 'Please enter a display name.';
    if (!selectedAction) errors.action = 'Please select an action.';
    setErrors(errors);

    if (Object.keys(errors).length > 0) return;

    const newFeature = {
      name: formData.name,
      description: formData.description,
      api_path: formData.apiPath,
      action: selectedAction,
      isDefault: formData.isDefault,
      displayName: formData.displayName,
    };

    try {
      const result = await createFeature(newFeature);
      addFeature(result);
      setFormData({
        name: '',
        apiPath: '',
        description: '',
        action: '',
        isDefault: false,
        displayName: '',
      });
      setSelectedAction('');
    } catch (error) {
      console.error('Error creating feature:', error);
    }
  };

  return (
    <>
      {/* <div className="form_header" style={{ marginTop: '0px' }}>Add Features</div> */}

      <Box
        // className="form-container"
        sx={{
          width: "calc(100% - 80px)",
          
          borderRadius: "10px",
          borderWidth: 1,
          padding: 4,
          margin: "0px 40px",
          boxSizing: "border-box",
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          gap: 4, // Adjust as per your spacing needs
          boxShadow: "-2px 2px 5px 0px #00000040",
        }}
        component="form"
        noValidate
        autoComplete="off"
      >
        <Box
          sx={{
            width: "100%",
            minHeight: 52,
            borderRadius: "8px",
            gap: 16,
            pt: 1.5, // 12px = 1.5 * 8px
            pr: 3,   // 24px = 3 * 8px
            pb: 1.5,
            pl: 3,
            background: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            color: "#FFFFFF", // Optional: add text color if needed
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          Create Features for Organizations
        </Box>

        <Grid container spacing={2} sx={{marginTop:"10px"}}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <InputLabel
              htmlFor="Name"
              sx={{ fontWeight: '400', fontSize: '24px', paddingBottom: '8px', color: 'black' }}
            >
              Name <StarIcon />
            </InputLabel>
            <TextField
              id="name"
              placeholder="Type Feature Name"
              name="name"
              value={formData.name}
              error={!!errors.name}
              helperText={errors.name}
              onChange={handleChange}
              fullWidth
              InputProps={{ style: { borderRadius: '16px' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderWidth: '2px', borderColor: 'black' },
                  '&:hover fieldset': { borderWidth: '2px' },
                  '&.Mui-focused fieldset': { borderWidth: '2px' },
                },
              }}
            />
          </Grid>

          {/* API Path */}
          <Grid item xs={12} sm={6}>
            <InputLabel
              htmlFor="API Path"
              sx={{ fontWeight: '400', fontSize: '24px', paddingBottom: '8px', color: 'black' }}
            >
              API Path <StarIcon />
            </InputLabel>
            <TextField
              id="apiPath"
              placeholder="Type API Path"
              name="apiPath"
              value={formData.apiPath}
              error={!!errors.apiPath}
              helperText={errors.apiPath}
              onChange={handleChange}
              fullWidth
              InputProps={{ style: { borderRadius: '16px' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderWidth: '2px', borderColor: 'black' },
                  '&:hover fieldset': { borderWidth: '2px' },
                  '&.Mui-focused fieldset': { borderWidth: '2px' },
                },
              }}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12} sm={6}>
            <InputLabel
              htmlFor="Description"
              sx={{ fontWeight: '400', fontSize: '24px', color: 'black', marginBottom: '8px' }}
            >
              Description
            </InputLabel>
            <TextField
              name="description"
              placeholder="Description"
              value={formData.description}
              fullWidth
              onChange={handleChange}
              multiline
              rows={1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  '& fieldset': { borderWidth: '2px', borderColor: 'black' },
                  '&:hover fieldset': { borderWidth: '2px' },
                  '&.Mui-focused fieldset': { borderWidth: '2px' },
                },
              }}
            />
          </Grid>

          {/* Display Name */}
          <Grid item xs={12} sm={6}>
            <InputLabel
              htmlFor="DisplayName"
              sx={{ fontWeight: '400', fontSize: '24px', paddingBottom: '8px', color: 'black' }}
            >
              Display Name <StarIcon />
            </InputLabel>
            <TextField
              id="displayName"
              placeholder="Type Display Name"
              name="displayName"
              value={formData.displayName}
              error={!!errors.displayName}
              helperText={errors.displayName}
              onChange={handleChange}
              fullWidth
              InputProps={{ style: { borderRadius: '16px' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderWidth: '2px', borderColor: 'black' },
                  '&:hover fieldset': { borderWidth: '2px' },
                  '&.Mui-focused fieldset': { borderWidth: '2px' },
                },
              }}
            />
          </Grid>

          {/* Action */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={{ color: 'black', fontFamily: 'Roboto', fontWeight: '400', marginBottom: '4px' }}
            >
              Operations <StarIcon sx={{ color: 'red' }} />
            </Typography>
            <Select
              labelId="operation-label"
              id="operation"
              name="action"
              fullWidth
              error={!!errors.action}
              value={selectedAction}
              onChange={handleActionChange}
              sx={{
                borderRadius: '16px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black', borderWidth: '2px' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
              }}
              displayEmpty
              renderValue={(selected) => (selected ? selected : <em>Select Operations</em>)}
            >
              <MenuItem value="">
                <em>Select Operations</em>
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="DELETE">DELETE</MenuItem>
              <MenuItem value="PATCH">PATCH</MenuItem>
            </Select>
            {errors.action && <FormHelperText>{errors.action}</FormHelperText>}
          </Grid>

          {/* Is Default */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDefault}
                  onChange={handleChange}
                  name="isDefault"
                  color="primary"
                  sx={{
                    color: 'black',
                    '&.Mui-checked': { color: 'rgba(249, 231, 25, 1)' },
                  }}
                />
              }
              label="Set as Default"
              sx={{
                '.MuiFormControlLabel-label': {
                  fontWeight: '400',
                  fontSize: '24px',
                  color: 'black',
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Submit Button */}
        <div className="addbutton_container" >
          <Button
            variant="contained"
            className="customButton_add"
            style={{ background: 'rgba(249, 231, 25, 1)', color: 'black' }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </Box>
    </>
  );
}

export default FeatureForm;
